import React from 'react';
// Billing support — formatters, mappers, permissões e erros (F1).
// Sem componentes React. Sem import/export. Nomes públicos prefixados com saBilling.
  const SA_BILLING_MANAGE_ROLES = {
    owner: true,
    admin: true,
    finance: true,
  };

  const SA_BILLING_CYCLE_LABELS = {
    monthly: 'Mensal',
    annual: 'Anual',
  };

  const SA_BILLING_PAYMENT_METHOD_LABELS = {
    not_informed: 'Não informado',
    manual: 'Manual',
    pix: 'Pix',
    bank_transfer: 'Transferência bancária',
    boleto: 'Boleto',
    card: 'Cartão',
    other: 'Outro',
  };

  const SA_BILLING_SUBSCRIPTION_STATUS_LABELS = {
    active: 'Ativa',
    ended: 'Encerrada',
    cancelled: 'Cancelada',
  };

  const SA_BILLING_INVOICE_DISPLAY_STATUS_LABELS = {
    open: 'Aberta',
    overdue: 'Vencida',
    paid: 'Marcada como paga',
    cancelled: 'Cancelada',
  };

  function saBillingCanManage(user) {
    if (!user || typeof user !== 'object') return false;
    if (user.is_platform_root !== true) return false;
    if (user.is_blocked === true) return false;
    const role = user.role;
    if (!role || typeof role !== 'string') return false;
    return SA_BILLING_MANAGE_ROLES[role] === true;
  }

  function saBillingFormatMoneyBRL(value) {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return '—';
    if (Array.isArray(value)) return '—';
    if (typeof value === 'object') return '—';

    let num;
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return '—';
      num = value;
    } else if (typeof value === 'string') {
      const normalized = value.trim();
      if (!normalized) return '—';
      // Apenas decimal estilo API ("1234.56"). Rejeita "1.234,56", "10abc", etc.
      if (!/^-?\d+(\.\d+)?$/.test(normalized)) return '—';
      num = Number(normalized);
      if (!Number.isFinite(num)) return '—';
    } else {
      return '—';
    }

    // Apresentação apenas — arredondamento de Intl é aceitável; não reutilizar em cálculo.
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num).replace(/\u00a0|\u202f/g, ' ');
  }

  function saBillingFormatDateBR(value) {
    if (value === null || value === undefined || value === '') return '—';
    const str = String(value).trim();
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
    if (!match) return '—';
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return '—';
    if (month < 1 || month > 12 || day < 1 || day > 31) return '—';

    // Construção local com números + round-trip (nunca new Date("YYYY-MM-DD")).
    const local = new Date(year, month - 1, day);
    if (
      local.getFullYear() !== year
      || local.getMonth() !== month - 1
      || local.getDate() !== day
    ) {
      return '—';
    }

    const dd = day < 10 ? `0${day}` : String(day);
    const mm = month < 10 ? `0${month}` : String(month);
    return `${dd}/${mm}/${year}`;
  }

  function saBillingFormatDateTimeBR(value) {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'string') {
      const trimmed = value.trim();
      // Date-only: nunca usar new Date("YYYY-MM-DD") (timezone).
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return saBillingFormatDateBR(trimmed);
      }
    }
    try {
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch (_err) {
      return '—';
    }
  }

  function saBillingFormatBillingCycle(code) {
    if (code === null || code === undefined || code === '') return '—';
    const key = String(code).trim();
    return SA_BILLING_CYCLE_LABELS[key] || key || 'Desconhecido';
  }

  function saBillingFormatPaymentMethod(code) {
    if (code === null || code === undefined || code === '') return '—';
    const key = String(code).trim();
    if (SA_BILLING_PAYMENT_METHOD_LABELS[key]) return SA_BILLING_PAYMENT_METHOD_LABELS[key];
    const lowered = key.toLowerCase();
    if (SA_BILLING_PAYMENT_METHOD_LABELS[lowered]) return SA_BILLING_PAYMENT_METHOD_LABELS[lowered];
    return key || 'Desconhecido';
  }

  function saBillingFormatSubscriptionStatus(code) {
    if (code === null || code === undefined || code === '') return '—';
    const key = String(code).trim();
    return SA_BILLING_SUBSCRIPTION_STATUS_LABELS[key] || key || 'Desconhecido';
  }

  function saBillingFormatInvoiceDisplayStatus(code) {
    if (code === null || code === undefined || code === '') return '—';
    const key = String(code).trim();
    return SA_BILLING_INVOICE_DISPLAY_STATUS_LABELS[key] || key || 'Desconhecido';
  }

  function saBillingMapOverview(payload) {
    if (!payload || typeof payload !== 'object') return {};

    const out = Object.assign({}, payload);

    if (payload.subscriptions && typeof payload.subscriptions === 'object') {
      const subs = payload.subscriptions;
      out.subscriptions = Object.assign({}, subs, {
        active_monthly_equivalent_formatted: saBillingFormatMoneyBRL(subs.active_monthly_equivalent),
      });
    }

    if (payload.invoices && typeof payload.invoices === 'object') {
      const inv = payload.invoices;
      out.invoices = Object.assign({}, inv, {
        open_amount_formatted: saBillingFormatMoneyBRL(inv.open_amount),
        overdue_amount_formatted: saBillingFormatMoneyBRL(inv.overdue_amount),
        outstanding_amount_formatted: saBillingFormatMoneyBRL(inv.outstanding_amount),
        paid_this_month_amount_formatted: saBillingFormatMoneyBRL(inv.paid_this_month_amount),
        cancelled_this_month_amount_formatted: saBillingFormatMoneyBRL(inv.cancelled_this_month_amount),
      });
    }

    // Compatibilidade com mock legado (mrr/arr/etc.) — só formata UI, não inventa métricas.
    if (Object.prototype.hasOwnProperty.call(payload, 'mrr')) {
      out.mrr_formatted = saBillingFormatMoneyBRL(payload.mrr);
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'arr')) {
      out.arr_formatted = saBillingFormatMoneyBRL(payload.arr);
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'overdue_revenue')) {
      out.overdue_revenue_formatted = saBillingFormatMoneyBRL(payload.overdue_revenue);
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'payments_received_month')) {
      out.payments_received_month_formatted = saBillingFormatMoneyBRL(payload.payments_received_month);
    }
    if (payload.updated_at != null) {
      out.updated_at_formatted = saBillingFormatDateTimeBR(payload.updated_at);
    }

    return out;
  }

  function saBillingMapGlobalSubscription(item) {
    if (!item || typeof item !== 'object') return {};

    const out = Object.assign({}, item);

    // Shape mock legado → campos canônicos de apresentação (sem mutar o input)
    if (out.plan_name_snapshot == null && item.plan != null) {
      out.plan_name_snapshot = item.plan;
    }
    if (out.plan_code_snapshot == null && item.plan_id != null) {
      out.plan_code_snapshot = item.plan_id;
    }
    if (out.billing_cycle == null && item.interval != null) {
      out.billing_cycle = item.interval;
    }
    if (out.next_due_date == null && item.next_charge != null) {
      out.next_due_date = item.next_charge;
    }
    if (out.payment_method == null && item.method != null) {
      out.payment_method = item.method;
    }

    out.status_label = saBillingFormatSubscriptionStatus(out.status);
    out.billing_cycle_label = saBillingFormatBillingCycle(out.billing_cycle);
    out.payment_method_label = saBillingFormatPaymentMethod(out.payment_method);
    out.amount_formatted = saBillingFormatMoneyBRL(out.amount);
    out.monthly_equivalent_formatted = saBillingFormatMoneyBRL(out.monthly_equivalent);
    out.next_due_date_formatted = saBillingFormatDateBR(out.next_due_date);
    out.updated_at_formatted = saBillingFormatDateTimeBR(out.updated_at);

    return out;
  }

  function saBillingMapGlobalInvoice(item) {
    if (!item || typeof item !== 'object') return {};

    const out = Object.assign({}, item);

    // Mock legado: status=overdue → status persistido open + display_status overdue
    if (item.status === 'overdue' && item.display_status == null) {
      out.status = 'open';
      out.display_status = 'overdue';
    } else if (out.display_status == null && out.status != null) {
      // Códigos desconhecidos (uncollectible, etc.) permanecem visíveis
      out.display_status = out.status;
    }

    if (out.payment_method == null && item.method != null) {
      out.payment_method = item.method;
    }

    out.display_status_label = saBillingFormatInvoiceDisplayStatus(out.display_status);
    out.payment_method_label = saBillingFormatPaymentMethod(out.payment_method);
    out.amount_formatted = saBillingFormatMoneyBRL(out.amount);
    out.due_date_formatted = saBillingFormatDateBR(out.due_date);
    out.paid_at_formatted = saBillingFormatDateTimeBR(out.paid_at);
    out.cancelled_at_formatted = saBillingFormatDateTimeBR(out.cancelled_at);

    return out;
  }

  function saBillingFormatError(error, fallbackMessage) {
    const fallback = fallbackMessage || 'Erro ao processar billing.';
    if (!error) return fallback;

    const status = error.status;
    const payload = error.payload && typeof error.payload === 'object' ? error.payload : {};
    const detail = typeof payload.detail === 'string' ? payload.detail : '';
    const payloadError = typeof payload.error === 'string' ? payload.error : '';
    const code = payload.code;

    if (status === 0) {
      return error.message || 'Não foi possível conectar ao servidor.';
    }
    if (status === 400) {
      return detail || payloadError || 'Filtro ou dados inválidos.';
    }
    if (status === 401) {
      return detail || payloadError || 'Sessão expirada. Faça login novamente.';
    }
    if (status === 403) {
      return detail || payloadError || 'Acesso negado.';
    }
    if (status === 404) {
      return detail || payloadError || 'Registro não encontrado.';
    }
    if (status === 409) {
      return detail || payloadError || 'Conflito de estado.';
    }
    if (status === 501 && code === 'billing_action_not_supported') {
      return detail || payloadError
        || error.message
        || 'Esta ação não está disponível no Billing administrativo atual.';
    }
    if (detail) return detail;
    if (payloadError) return payloadError;
    if (typeof error.message === 'string' && error.message) return error.message;
    return fallback;
  }

export {
  saBillingCanManage,
  saBillingFormatBillingCycle,
  saBillingFormatDateBR,
  saBillingFormatDateTimeBR,
  saBillingFormatError,
  saBillingFormatInvoiceDisplayStatus,
  saBillingFormatMoneyBRL,
  saBillingFormatPaymentMethod,
  saBillingFormatSubscriptionStatus,
  saBillingMapGlobalInvoice,
  saBillingMapGlobalSubscription,
  saBillingMapOverview,
};
